import React, { useState } from 'react';
import classNames from 'classnames/bind';
import { format, setDefaultOptions } from 'date-fns';
import { de } from 'date-fns/locale';
import type { ReducerAction } from '../../../../types/navigationIndex.types';
import type { DoctorSlot } from '../../../../types/user.types';
import { Button, Icon, RadioButton } from '../../../atoms';
import { ProgressStep, ProgressSteps } from '../../../organisms/ProgressSteps/ProgressSteps';
import classes from './TimeStep.module.scss';

const cx = classNames.bind(classes);

type TimeStepProps = {
  isFirstAppointment: boolean;
  isOffline: boolean;
  setCurrentPage: React.Dispatch<ReducerAction>;
  appointmentInfo: any;
  // eslint-disable-next-line no-unused-vars
  setAppointmentInfo: (value: any) => void;
};
const stepsOnlineAppointmentClasses = cx({ StepTwoOnlineAppointment: true, StepsContainer: true });
const selectedDateContainerClasses = cx({ SelectedDateContainer: true });
const selectedDateClasses = cx({ SelectedDate: true });
const slotsContainerClasses = cx({ SlotsContainer: true });
const slotClasses = cx({ Slot: true });
const slotSelectedClasses = cx({ Slot: true, Selected: true });
const slotTextClasses = cx({ SlotText: true });
const slotDoctorClasses = cx({ SlotText: true, SlotDoctor: true });
const slotButtonClasses = cx({ SlotButton: true });

function sortDates(array: Array<DoctorSlot>) {
  return array.sort((a: DoctorSlot, b: DoctorSlot) => {
    if (new Date(a.start) < new Date(b.start)) {
      return -1;
    }
    if (new Date(a.start) > new Date(b.start)) {
      return 1;
    }

    return 0;
  });
}

export function TimeStep({
  isFirstAppointment,
  isOffline,
  setCurrentPage,
  appointmentInfo,
  setAppointmentInfo,
}: TimeStepProps) {
  // set locale for date-fns
  setDefaultOptions({ locale: de });

  const [selectedSlot, setSelectedSlot] = useState<DoctorSlot | undefined>();
  const firstAppointmentDuration =
    typeof appointmentInfo?.firstAppointmentDuration === 'number' ? appointmentInfo.firstAppointmentDuration : 20;

  const getDoctorName = (id: string) => {
    let name = '';
    let title = '';
    appointmentInfo.doctors.map((item: any) => {
      if (item.id === parseInt(id, 10)) {
        if (item.title === 'professor') {
          title = 'Prof.';
        } else if (item.title) {
          title = 'Dr.';
        }
        name = `${title} ${item.firstname} ${item.lastname}`;
      }

      return item;
    });

    return name;
  };

  const getDoctorStamp = (id: string) => {
    const doctor = appointmentInfo.doctors.find((item: any) => {
      if (item.id === parseInt(id, 10)) {
        return true;
      }

      return false;
    });

    if (doctor) {
      return doctor.stamp;
    }

    return '';
  };

  const next = () => {
    if (selectedSlot) {
      setAppointmentInfo({
        ...appointmentInfo,
        doctorName: getDoctorName(selectedSlot.doctorId),
        doctorId: selectedSlot.doctorId,
        selectedSlot,
        practiceId: isOffline ? selectedSlot.practiceId : appointmentInfo.practiceId,
        doctorStamp: getDoctorStamp(selectedSlot.doctorId),
      });
      const reducerType = {
        type: '[UPDATE]',
        payload: { appointmentIndex: 3 },
      };
      setCurrentPage(reducerType);
    }
  };

  const sortedSlotsForDay = sortDates(appointmentInfo.doctorSlots);

  const SlotInterval = ({ slot }: { slot: DoctorSlot }) => {
    return (
      <div className={classes.SlotInterval}>
        {new Date(slot.start).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })}{' '}
        -{' '}
        {new Date(slot.end).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })}{' '}
        Uhr
      </div>
    );
  };

  const handleDurationChange = (duration: 20 | 30) => {
    if (duration === firstAppointmentDuration) {
      return;
    }

    setSelectedSlot(undefined);
    setAppointmentInfo({
      ...appointmentInfo,
      firstAppointmentDuration: duration,
      selectedSlot: undefined,
      doctorSlots: [],
    });
    setCurrentPage({
      type: '[UPDATE]',
      payload: { appointmentIndex: 1 },
    });
  };

  return (
    <div className={stepsOnlineAppointmentClasses}>
      <div className='max-w-[550px] mx-auto mb-4'>
        <ProgressSteps>
          {isOffline && isFirstAppointment ? <ProgressStep title='Standort' active /> : null}
          <ProgressStep title='Datum' active />
          <ProgressStep title='Uhrzeit' active />
          <ProgressStep title='Information' />
          <ProgressStep title='Bestätigung' />
          <ProgressStep title='Zahlung' />
        </ProgressSteps>
      </div>

      {isFirstAppointment ? (
        <div className='mb-5'>
          <div className='text-primary font-semibold mb-3 text-center'>Wie lange soll Ihr Erstgespräch dauern?</div>
          <div className='flex items-center gap-4 justify-center'>
            <RadioButton
              id='first-appointment-duration-time-step-20'
              name='first-appointment-duration-time-step'
              checked={firstAppointmentDuration === 20}
              value={20}
              onChange={() => handleDurationChange(20)}
            >
              20 Minuten
            </RadioButton>
            <RadioButton
              id='first-appointment-duration-time-step-30'
              name='first-appointment-duration-time-step'
              checked={firstAppointmentDuration === 30}
              value={30}
              onChange={() => handleDurationChange(30)}
            >
              30 Minuten
            </RadioButton>
          </div>
        </div>
      ) : null}

      <div className='text-primary font-semibold mb-4'>
        Wann soll Ihr {isOffline ? 'Vor-Ort' : 'Online'}
        {isFirstAppointment ? ' Erstgespräch' : ' Termin'} stattfinden?
      </div>

      <div className={selectedDateContainerClasses}>
        <div className={selectedDateClasses}>
          Verfügbare Termine am <b>{format(appointmentInfo.selectedDate, 'PPP')}</b>
        </div>
        <a
          onClick={() => {
            const reducerType = {
              type: '[UPDATE]',
              payload: { appointmentIndex: 1 },
            };
            setCurrentPage(reducerType);
          }}
        >
          Datum ändern
        </a>
      </div>
      <div style={{ width: '100%' }}>
        <h4>Wählen Sie bitte die Uhrzeit</h4>
      </div>
      <div className={slotsContainerClasses}>
        {sortedSlotsForDay.map((slot: DoctorSlot) => (
          <div
            className={selectedSlot === slot ? slotSelectedClasses : slotClasses}
            key={`${slot.doctorId}-${slot.start}`}
            onClick={() => setSelectedSlot(slot)}
            role='button'
            aria-pressed={selectedSlot === slot ? 'true' : 'false'}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setSelectedSlot(slot);
              }
            }}
          >
            <div>
              <div className={classes.selected}>
                <div className={slotTextClasses}>
                  <SlotInterval slot={slot} />
                </div>
                <div className={slotDoctorClasses}>
                  <Icon
                    icon='user'
                    size={16}
                    color={
                      selectedSlot && slot.start === selectedSlot.start && slot.doctorId === selectedSlot.doctorId
                        ? '#ffffff'
                        : '#000000'
                    }
                  />
                  Arzt: {getDoctorName(slot.doctorId)}
                </div>
              </div>
              {selectedSlot && slot.start === selectedSlot.start && slot.doctorId === selectedSlot.doctorId && (
                <Button onClick={next} className={slotButtonClasses} inlineBlock={true}>
                  Termin buchen
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
